import { useStorage } from "@vueuse/core";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "/@/tour/tour.css";
import {
  buildDashboardSteps,
  filterAvailableSteps,
  shouldOfferTour,
  TourStep,
} from "/@/tour/tour-steps";

// Single shared ref (same singleton reasoning as useBoardedStorage:
// separate @vueuse refs on one key don't sync in-tab).
const tourStorage = useStorage("nibOnlineSubmissions_tour", {
  completed: false,
});

// Visible, not merely present: the desktop nav/user-menu are `hidden sm:flex`
// — in the DOM on mobile but display:none, so querySelector alone would tour
// invisible elements.
const isVisiblyRendered = (selector: string): boolean => {
  const el = document.querySelector(selector);
  return !!el && el.getClientRects().length > 0;
};

// The Pinky step must reflect the widget actually rendering, not our
// injection attempt (window.chtlConfig is set before embed.js loads and
// stays set under adblock/outage). Chatling's rendered DOM (verified
// against the live widget 2026-07-20): #chtl-chat-icon-container (bubble)
// and iframe#chtl-chat-iframe (chat window, src embed.chatling.ai). The
// broader src match is kept as a fallback for vendor markup changes.
const hasChatlingWidget = (): boolean =>
  Array.from(
    document.querySelectorAll(
      '#chtl-chat-icon-container, #chtl-chat-iframe, iframe[src*="chatling"]'
    )
  ).some((el) => el.getClientRects().length > 0);

function toDriveSteps(steps: TourStep[]) {
  return steps.map((s) => ({
    element: s.target ?? undefined,
    popover: {
      title: s.title,
      description: s.content,
      side: s.side,
    },
  }));
}

function run(): void {
  const steps = filterAvailableSteps(
    buildDashboardSteps({ hasChatling: hasChatlingWidget() }),
    isVisiblyRendered
  );
  // Only centered steps left means the dashboard isn't really on screen —
  // don't show an empty overlay.
  if (!steps.some((s) => s.target !== null)) return;

  const d = driver({
    showProgress: true,
    popoverClass: "nib-tour",
    nextBtnText: "Next",
    prevBtnText: "Back",
    doneBtnText: "Finish",
    steps: toDriveSteps(steps),
    onDestroyed: () => {
      // Fires on finish AND on skip/close/Esc — either way, never
      // auto-offer again (spec decision).
      tourStorage.value.completed = true;
    },
  });
  d.drive();
}

export default function useTour() {
  const startTour = () => {
    run();
  };

  /**
   * First-visit auto-offer. Returns a cancel function the caller MUST wire
   * to onUnmounted — without it, navigating away within the delay would
   * start the tour on whatever page comes next.
   */
  const offerTourOnce = (): (() => void) => {
    if (!shouldOfferTour(tourStorage.value)) return () => {};
    // Small delay lets the dashboard finish rendering (checklist fetches).
    // Re-check at fire time: the user may have run (and closed) the tour via
    // the help button within the delay window.
    const timer = window.setTimeout(() => {
      if (shouldOfferTour(tourStorage.value)) run();
    }, 600);
    return () => window.clearTimeout(timer);
  };

  const resetTour = () => {
    tourStorage.value.completed = false;
  };

  return { startTour, offerTourOnce, resetTour };
}
